import React, { useMemo, useState } from "react";
import {
  Row,
  Col,
  Card,
  Badge,
  Alert,
  Button,
  Modal,
  Carousel,
} from "react-bootstrap";
import {
  FaCouch,
  FaCar,
  FaWeightHanging,
  FaExclamationTriangle,
  FaRulerCombined,
  FaBox,
  FaCalendar,
  FaMapMarkerAlt,
  FaPaperPlane,
  FaTimes,
  FaExpand,
  FaRoute,
  FaClock,
  FaPallet,
  FaTruck,
  FaClipboardList,
  FaDog,
} from "react-icons/fa";
import { TbListDetails } from "react-icons/tb";
import { HiWrenchScrewdriver } from "react-icons/hi2";
import { useTranslation } from "react-i18next";
import { RouteMap } from "../../RouteMap";
import { maskPhoneNumbers, parseCoordinates } from "../../../utils/helpers";
import "./DarkTheme.css";

import paletteImg from "../../../assets/images/driver_transport_icon/palette.png";
import paketeImg from "../../../assets/images/driver_transport_icon/pakete.png";
import documentImg from "../../../assets/images/driver_transport_icon/document.png";
import furnitureImg from "../../../assets/images/driver_transport_icon/furniture.png";
import electricalImg from "../../../assets/images/driver_transport_icon/electrical.png";
import tiresImg from "../../../assets/images/driver_transport_icon/tires.png";
import refrigeratorImg from "../../../assets/images/driver_transport_icon/refrigerator.png";
import buildingMaterialsImg from "../../../assets/images/driver_transport_icon/building_materials.png";
import movingImg from "../../../assets/images/driver_transport_icon/moving.png";
import bicycleImg from "../../../assets/images/driver_transport_icon/bicycle.png";
import motorcycleImg from "../../../assets/images/driver_transport_icon/motorcycle.png";
import carImg from "../../../assets/images/driver_transport_icon/car.png";
import animalTransportImg from "../../../assets/images/driver_transport_icon/animal_transport.png";
import otherImg from "../../../assets/images/driver_transport_icon/other.png";
import fastImg from "../../../assets/images/driver_transport_icon/rocket.png";
import regularImg from "../../../assets/images/driver_transport_icon/pakete.png";
import { RiPinDistanceFill } from "react-icons/ri";

// Dark Theme Items Section
const DarkItemsSection = ({
  itemTypes,
  numItems,
  dimensions,
  itemWeights,
  weight,
  making_year,
  driving,
  isCar = false,
  isAnimal = false,
}) => {
  const { t } = useTranslation();

  return (
    <div className="mb-3">
      {itemTypes?.map((type, index) => (
        <div
          key={index}
          className="dark-shipment-item-row flex-column align-items-start"
          style={{ gap: "0.5rem" }}
        >
          <div className="d-flex align-items-center gap-2 w-100">
            {isAnimal ? (
              <FaDog className="dark-icon-muted" />
            ) : (
              <FaPallet className="dark-icon-muted" />
            )}
            <span>
              {numItems?.[index] || 1} × {type}
            </span>
          </div>
          <div className="d-flex flex-wrap gap-3 ps-4">
            {dimensions?.[index] && (
              <span className="dark-text-muted" style={{ fontSize: "0.85rem" }}>
                <FaRulerCombined className="me-1" /> {dimensions?.[index]} cm
              </span>
            )}
            {itemWeights?.[index] && (
              <span className="dark-text-muted" style={{ fontSize: "0.85rem" }}>
                <FaWeightHanging className="me-1" /> {itemWeights?.[index]} kg
              </span>
            )}
            {weight?.[index] && (
              <span className="dark-text-muted" style={{ fontSize: "0.85rem" }}>
                <FaWeightHanging className="me-1" /> {weight?.[index]} kg
              </span>
            )}
            {isCar && making_year?.[index] && (
              <span className="dark-text-muted" style={{ fontSize: "0.85rem" }}>
                <FaCalendar className="me-1" /> {making_year?.[index]}
              </span>
            )}
            {isCar && driving?.[index] && (
              <span className="dark-text-muted" style={{ fontSize: "0.85rem" }}>
                <HiWrenchScrewdriver className="me-1" />
                {t("driverCard.transportDetails.driving")}:{" "}
                {driving?.[index] === "1" ? t("yes") : t("no")}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// Dark Theme Additional Items Section
const AdditionalSection = ({ itemTypes }) => {
  const { t } = useTranslation();

  const PAYMENT_TERM_OPTIONS = [
    { value: "on_delivery", label: t("delivery_summary.paymentTerms.onDelivery", "Bei Lieferung") },
    { value: "7_days", label: t("delivery_summary.paymentTerms.after7Days", "7 Tage nach Lieferung") },
    { value: "14_days", label: t("delivery_summary.paymentTerms.after14Days", "14 Tage nach Lieferung") },
    { value: "30_days", label: t("delivery_summary.paymentTerms.after30Days", "30 Tage nach Lieferung") },
    { value: "3_days", label: t("delivery_summary.paymentTerms.after3Days", "3 Tage nach Lieferung") },
    { value: "by_agreement", label: t("delivery_summary.paymentTerms.byAgreement", "Nach Vereinbarung") },
  ];

  return (
    <div className="mb-3">
      <div className="details-list">
        <div className="d-flex align-items-center gap-2 w-100 pt-3">
          <h6 className="small-muted">
            {t("delivery_summary.additional_details")}
          </h6>
        </div>

        {itemTypes.delivery_as && (
          <div className="detail-row">
            <div>{t("delivery_summary.listing_as")}</div>
            <div className="small-muted">{t(itemTypes.delivery_as)}</div>
          </div>
        )}

        {itemTypes.malfunction === "1" && (
          <div className="detail-row">
            <div>{t("delivery_summary.fragile")}</div>
            <div className="small-muted">
              {t("yes")}{itemTypes.malfunction_details ? ` : ${itemTypes.malfunction_details}` : ""}
            </div>
          </div>
        )}
        {itemTypes.malfunction === "0" && (
          <div className="detail-row">
            <div>{t("delivery_summary.fragile")}</div>
            <div className="small-muted">{t("no")}</div>
          </div>
        )}

        {itemTypes.special_treatment === "1" && (
          <div className="detail-row">
            <div>{t("delivery_summary.special_handling")}</div>
            <div className="small-muted">
              {t("yes")}{itemTypes.special_treatment_details ? ` : ${itemTypes.special_treatment_details}` : ""}
            </div>
          </div>
        )}
        {itemTypes.special_treatment === "0" && (
          <div className="detail-row">
            <div>{t("delivery_summary.special_handling")}</div>
            <div className="small-muted">{t("no")}</div>
          </div>
        )}

        {itemTypes.item_breakable === "1" && (
          <div className="detail-row">
            <div>{t("driverCard.transportDetails.itemBreakable")}</div>
            <div className="small-muted">
              {t("yes")}{itemTypes.item_breakable_details ? ` : ${itemTypes.item_breakable_details}` : ""}
            </div>
          </div>
        )}
        {itemTypes.item_breakable === "0" && (
          <div className="detail-row">
            <div>{t("driverCard.transportDetails.itemBreakable")}</div>
            <div className="small-muted">{t("no")}</div>
          </div>
        )}

        {itemTypes.load_helping && itemTypes.load_helping !== "keine" && (
          <div className="detail-row">
            <div>{t("delivery_summary.help_carry")}</div>
            <div className="small-muted">
              {itemTypes.load_helping === "0"
                ? t("delivery_summary.no_help")
                : itemTypes.load_helping === "1"
                  ? t("delivery_summary.partial_help")
                  : itemTypes.load_helping === "2"
                    ? t("delivery_summary.full_help")
                    : itemTypes.load_helping}
            </div>
          </div>
        )}

        {(itemTypes.loading_floor > 0 || itemTypes.unloading_floor > 0) && (
          <>
            <div className="small-muted mt-2">
              {t("delivery_summary.pickup_floor")}:{" "}
              <strong>{itemTypes.loading_floor}</strong>
              &nbsp; {t("delivery_summary.elevator")}:{" "}
              <strong>
                {itemTypes.loading_elevator
                  ? t("delivery_summary.yes")
                  : t("delivery_summary.no")}
              </strong>
            </div>
            <div className="small-muted mt-1">
              {t("delivery_summary.delivery_floor")}:{" "}
              <strong>{itemTypes.unloading_floor}</strong>
              &nbsp; {t("delivery_summary.elevator")}:{" "}
              <strong>
                {itemTypes.unloading_elevator
                  ? t("delivery_summary.yes")
                  : t("delivery_summary.no")}
              </strong>
            </div>
          </>
        )}

        {itemTypes.payment_term && (
          <div className="detail-row">
            <div>{t("delivery_summary.paymentTerm.title", "Zahlungsziel")}</div>
            <div className="small-muted">
              {PAYMENT_TERM_OPTIONS.find(
                (option) => option.value === itemTypes.payment_term
              )?.label || "—"}
            </div>
          </div>
        )}

        {/* {(itemTypes.loading_date_start || itemTypes.loading_date) && (
          <div className="detail-row">
            <div>{t("driverCard.transportDetails.loadingDateStart")}</div>
            <div className="small-muted">
              {itemTypes.loading_date_start || itemTypes.loading_date}
              {(itemTypes.loading_time_start || itemTypes.loading_time) && (
                <span className="ms-1">
                  {itemTypes.loading_time_start || itemTypes.loading_time}
                </span>
              )}
            </div>
          </div>
        )}

        {itemTypes.loading_date_end && (
          <div className="detail-row">
            <div>{t("driverCard.transportDetails.loadingDateEnd")}</div>
            <div className="small-muted">
              {itemTypes.loading_date_end}
              {itemTypes.loading_time_end && (
                <span className="ms-1">{itemTypes.loading_time_end}</span>
              )}
            </div>
          </div>
        )}

        {(itemTypes.unloading_date_start || itemTypes.unloading_date) && (
          <div className="detail-row">
            <div>{t("driverCard.transportDetails.unloadingDateStart")}</div>
            <div className="small-muted">
              {itemTypes.unloading_date_start || itemTypes.unloading_date}
              {(itemTypes.unloading_time_start || itemTypes.unloading_time) && (
                <span className="ms-1">
                  {itemTypes.unloading_time_start || itemTypes.unloading_time}
                </span>
              )}
            </div>
          </div>
        )}

        {itemTypes.unloading_date_end && (
          <div className="detail-row">
            <div>{t("driverCard.transportDetails.unloadingDateEnd")}</div>
            <div className="small-muted">
              {itemTypes.unloading_date_end}
              {itemTypes.unloading_time_end && (
                <span className="ms-1">{itemTypes.unloading_time_end}</span>
              )}
            </div>
          </div>
        )} */}
      </div>
    </div>
  );
};

// Dark Theme Image Gallery
const DarkImageGallery = ({ images, className = "" }) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { t } = useTranslation();

  if (!images || images.length === 0) {
    return null;
  }

  const handleImageClick = (index) => {
    setSelectedIndex(index);
    setShowModal(true);
  };

  return (
    <>
      <div className={`${className}`}>
        <div className="d-flex flex-wrap gap-2">
          {images.slice(0, 4).map((image, index) => (
            <div
              key={index}
              className="position-relative"
              style={{ cursor: "pointer" }}
              onClick={() => handleImageClick(index)}
            >
              <img
                src={image}
                alt={`Transport ${index + 1}`}
                style={{
                  width: "60px",
                  height: "60px",
                  objectFit: "cover",
                  borderRadius: "8px",
                  border: "1px solid #444",
                }}
              />
              {index === 3 && images.length > 4 && (
                <div
                  className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                  style={{
                    background: "rgba(0,0,0,0.7)",
                    borderRadius: "8px",
                    color: "#fff",
                    fontWeight: "600",
                  }}
                >
                  +{images.length - 4}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="lg"
        centered
      >
        <Modal.Header className="border-0 bg-dark">
          <Modal.Title className="text-white">
            {t("driverCard.transportDetails.images")} ({selectedIndex + 1}/
            {images.length})
          </Modal.Title>
          <Button
            variant="link"
            className="text-white p-0"
            onClick={() => setShowModal(false)}
          >
            <FaTimes size={20} />
          </Button>
        </Modal.Header>
        <Modal.Body className="bg-dark text-center p-0">
          <Carousel
            activeIndex={selectedIndex}
            onSelect={setSelectedIndex}
            interval={null}
          >
            {images.map((image, index) => (
              <Carousel.Item key={index}>
                <img
                  src={image}
                  alt={`Transport ${index + 1}`}
                  className="img-fluid"
                  style={{ maxHeight: "70vh", objectFit: "contain" }}
                />
              </Carousel.Item>
            ))}
          </Carousel>
        </Modal.Body>
      </Modal>
    </>
  );
};

const transportImages = {
  palette: paletteImg,
  pakete: paketeImg,
  document: documentImg,
  furniture: furnitureImg,
  electrical: electricalImg,
  tires: tiresImg,
  refrigerator: refrigeratorImg,
  "building materials": buildingMaterialsImg,
  moving: movingImg,
  bicycle: bicycleImg,
  motorcycle: motorcycleImg,
  car: carImg,
  "animal transport": animalTransportImg,
  other: otherImg,
  fast: fastImg,
  regular: regularImg,
  furnitures: furnitureImg,
  cars: carImg,
};

const TransportImage = ({ type, item, className = "", size = 100 }) => {
    const getImageSrc = () => {
      // 1. Try shipment_data (for regular/fast orders)
      const shipmentData = item.shipment_data || item.booking?.shipment_data;
      if (shipmentData && shipmentData.length > 0) {
        const firstShipment = shipmentData[0].shipment_data?.toLowerCase();
        if (firstShipment) {
          // Normalization: German terms -> English keys
          let normalizedKey = firstShipment === "pallete" ? "palette" : firstShipment;
          normalizedKey = normalizedKey === "dokument" ? "document" : normalizedKey;
          normalizedKey = normalizedKey === "baustoffe" ? "building materials" : normalizedKey;
          normalizedKey = normalizedKey === "elektrisch" ? "electrical" : normalizedKey;

          if (transportImages[normalizedKey]) {
            return transportImages[normalizedKey];
          }
        }
      }

      // 2. Try transport_type
      if (item?.transport_type) {
        const key = item.transport_type.toLowerCase();
        if (transportImages[key]) return transportImages[key];
      }

      if (item.transport?.transport_type) {
        const key = item.transport.transport_type.toLowerCase();
        if (transportImages[key]) return transportImages[key];
      }

      // 3. Try item type
      if (item?.type) {
        const key = item.type.toLowerCase();
        if (transportImages[key]) return transportImages[key];
      }

      return transportImages[type] || transportImages.other;
    };

    const imageSrc = getImageSrc();

    return (
      <img
        src={imageSrc}
        alt={type}
        className={className}
        style={{
          width: `${size}px`,
          height: `${size}px`,
          objectFit: "contain",
          // filter: "invert(1)"
        }}
        onError={(e) => {
          e.target.src = transportImages.other;
        }}
      />
    );
  };

// Main Dark Theme Transport Details Component
const DarkTransportDetailsPage = ({ item, type, onSendOffer, onClose, onChat, unreadCount, showChat = false }) => {
  const { t } = useTranslation();

  const isCar = type === "cars";
  // Check if this is animal transport
  const isAnimal =
    item.vehicle_type?.toLowerCase().includes("animal") ||
    item.transport_type?.toLowerCase().includes("animal");
  const itemsCount = item.item_type?.length || 0;


  const fromCoords = useMemo(
    () => parseCoordinates(item.booking_from_lat_long),
    [item.booking_from_lat_long],
  );
  const toCoords = useMemo(
    () => parseCoordinates(item.booking_to_lat_long),
    [item.booking_to_lat_long],
  );

  if (!fromCoords || !toCoords) {
    return (
      <div className="text-danger text-center p-3">
        {t("driverCard.general.invalidCoords")}
      </div>
    );
  }

  return (
    <div className="dark-three-columns">
      {/* Section 1: Route */}
      <div className="dark-section">
        <div className="dark-section-title">
          <FaRoute />
          <span>{t("driverCard.general.route")}</span>
        </div>
        <div className="dark-section-body">
          {/* Map */}
          <div className="dark-map-wrapper">
            {fromCoords && toCoords &&(
              <RouteMap fromCoords={fromCoords} toCoords={toCoords} />
            )}
          </div>

          {/* pickup & delivery */}
          <div className="dark-route-stats py-3 justify-content-between flex-wrap">

            <div className="dark-route-stat gap-2 flex-row" style={{borderRight: "1px solid rgb(212 212 212)"}}>
              <FaMapMarkerAlt className="stat-icon text-danger" style={{ fontSize: "25px" }} />
              <div className="p-2 rounded">
                <div className="text-center">
                  <span className="stat-value d-block">{item.booking_from}</span>
                  <span className="stat-label">
                    {t("driverCard.general.pickup")}
                  </span>
                </div>
              </div>
            </div>

            <div className="dark-route-stat gap-2 flex-row">
              <FaMapMarkerAlt className="stat-icon text-success" style={{ fontSize: "25px" }} />
              <div className="p-2 rounded">
                <div className="text-center">
                  <span className="stat-value d-block">{item.booking_to}</span>
                  <span className="stat-label">
                    {t("driverCard.general.delivery")}
                  </span>
                </div>
              </div>
            </div>

          </div>

          {/* Route Stats */}
          <div className="dark-route-stats justify-content-between">

            <div className="dark-route-stat gap-2 flex-row">
              <RiPinDistanceFill className="stat-icon" style={{ fontSize: "25px" }} />
              <div className="text-center">
                <span className="stat-value d-block">{(item.distance || "0 km").replace("km", " km")}</span>
                <span className="stat-label">
                  {t("driverCard.general.distance")}
                </span>
              </div>
            </div>

            <div className="dark-route-stat gap-2 flex-row">
              <FaClock className="stat-icon" style={{ fontSize: "25px" }} />
              <div className="text-center">
                <span className="stat-value d-block">{item.total_duration || "-"}</span>
                <span className="stat-label">
                  {t("driverCard.general.duration")}
                </span>
              </div>
            </div>

          </div>
          
        </div>
      </div>

      {/* Section 2: Vehicle & Actions */}
      <div className="dark-section">
        <div className="dark-section-title">
          <FaTruck />
          <span>{t("driverCard.orderDetails.vehicleAndActions")}</span>
        </div>
        <div className="dark-section-body">
          {/* Vehicle Info */}
          <div className="dark-vehicle-info-block">
            <div className="dark-info-label">
              {t("driverCard.general.items2")}
            </div>
            <div className="dark-info-value">
              {/* {isAnimal ? <FaDog /> : isCar ? <FaCar /> : <FaCouch />} */}
              <TransportImage
                type={item?.transport_type}
                item={item}
                size={32}
              />
              {item.vehicle_type ||
                t(item.item_type) ||
                t("driverCard.general.notAvailable")}
            </div>
          </div>

          {/* CTA Button */}
          <button
            className="dark-btn-cta py-1"
            onClick={() => onSendOffer(item)}
          >
            <FaPaperPlane />
            {type === "fast" || type === "regular"
              ? t("driverCard.buttons.sendOffer")
              : t("driverCard.buttons.makeOffer")}
          </button>

          <Button
            variant="success"
            className="btn btn-success w-100"
            onClick={() => onChat(item)}
          >
            💬 {t("driverOffer.buttons.chat")}
            {unreadCount > 0 && (
              <Badge
                bg="danger"
                className="position-absolute top-0 start-100 translate-middle"
                style={{ fontSize: "0.6rem" }}
              >
                {unreadCount}
              </Badge>
            )}
          </Button>

          {/* Price */}
          {/* <div className="dark-price-block">
            <span className="dark-price-amount">
              {item.discounted_price || item.price || "0"} EUR
            </span>
            <span className="dark-price-suffix">(Netto)</span>
          </div> */}
        </div>
      </div>

      {/* Section 3: Shipment Details */}
      <div className="dark-section">
        <div className="dark-section-title">
          <FaClipboardList />
          <span>{t("driverCard.orderDetails.shipmentDetails")}</span>
        </div>
        <div className="dark-section-body">
          {/* Shipment Info - Show total items count */}
          <div className="dark-shipment-item-row">
            {isAnimal ? (
              <FaDog className="dark-icon-muted" />
            ) : (
              <FaPallet className="dark-icon-muted" />
            )}
            <span>
              {itemsCount}{" "}
              {itemsCount === 1
                ? t("driverCard.general.item")
                : t("driverCard.general.items")}
            </span>
          </div>

          {/* Dimensions */}
          {/* {item.dimensions && item.dimensions[0] && (
            <div className="dark-shipment-item-row">
              <FaRulerCombined className="dark-icon-muted" />
              <span className="dark-text-white">{item.dimensions[0]} cm</span>
            </div>
          )} */}

          {/* Items List */}
          {item.item_type && item.item_type.length > 0 && (
            <>
              <DarkItemsSection
                itemTypes={item.item_type}
                numItems={item.num_items}
                dimensions={item.dimensions}
                itemWeights={item.item_weight}
                weight={item.weight}
                making_year={item.making_year}
                driving={item.driving}
                isCar={isCar}
                isAnimal={isAnimal}
              />

              {/* Weight */}
              <div className="dark-shipment-item-row">
                <FaWeightHanging className="dark-icon-muted" />
                <span>
                  {item.total_weight || "0"} kg
                </span>
              </div>

              <AdditionalSection itemTypes={item} />
            </>
          )}

          {/* Notes */}
          <div className="dark-notes-block">
            <label>{t("driverCard.orderDetails.notes")}</label>
            <textarea
              className="dark-notes-textarea"
              placeholder={maskPhoneNumbers(item.description) || t("driverRequest.general.noData")}
              readOnly
              value={maskPhoneNumbers(item.description) || ""}
            />
          </div>

          {/* Images */}
          {item.images && item.images.length > 0 && (
            <div className="mt-3">
              <DarkImageGallery images={item.images} />
            </div>
          )}

          {/* Close Button - Desktop only */}
          <Button
            className="w-100 py-2 fw-bold mt-3 d-md-block d-none"
            variant="outline-danger"
            onClick={onClose}
          >
            {t("driverCard.buttons.close")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(DarkTransportDetailsPage);
